import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import akLogo from "@/assets/ak-logo.png";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

function makeForgotSchema(t: (k: string) => string) {
  return z.object({
    email: z.string().trim().email(t("auth.zod.emailInvalid")).max(255),
  });
}

export const Route = createFileRoute("/forgot-password")({
  component: ForgotPasswordPage,
  head: () => ({
    meta: [
      { title: "Reset your password · AdKavi" },
      { name: "description", content: "Request a password reset link for your AdKavi account." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function ForgotPasswordPage() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = makeForgotSchema(t).safeParse({ email });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(parsed.data.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (error && !error.message.toLowerCase().includes("rate")) {
      console.error(error);
    }
    setSent(true);
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-6 bg-background">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <img src={akLogo} alt="AdKavi logo" className="w-10 h-10 rounded-xl object-cover" />
          <span className="text-2xl font-bold">AdKavi</span>
        </Link>

        <div className="rounded-2xl border border-border/50 bg-card/50 backdrop-blur p-8">
          <h1 className="text-2xl font-bold mb-2">{t("forgot.title")}</h1>
          <p className="text-sm text-muted-foreground mb-6">
            {t("forgot.desc")}
          </p>

          {sent ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-border/50 bg-background/40 p-4 text-sm text-muted-foreground">
                {t("forgot.sentPrefix")} <span className="text-foreground font-medium">{email}</span>{t("forgot.sentSuffix")}
              </div>
              <Link to="/auth" className="inline-flex items-center text-sm text-primary hover:underline">
                <ArrowLeft className="w-4 h-4 mr-1" /> {t("forgot.back")}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t("auth.email")}</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                {loading ? t("forgot.sending") : t("forgot.send")}
              </Button>
              <Link to="/auth" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="w-4 h-4 mr-1" /> {t("forgot.back")}
              </Link>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
